import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import './SectorsPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');
const STATUS_OPTIONS = ['Aktif', 'Pasif'];

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Sektorler',
    pageDescription: 'EVDS NACE Rev.2 alt sektor tanimlari.',
    newSector: 'Yeni sektor',
    sectorGroup: 'Sektor Grubu',
    sector: 'Sektor',
    code: 'Kod',
    status: 'Durum',
    action: 'Aksiyon',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz sektor tanimi bulunamadi.',
    edit: 'Duzenle',
    delete: 'Sil',
    close: 'Kapat',
    save: 'Kaydet',
    update: 'Guncelle',
    cancel: 'Iptal',
    saving: 'Kaydediliyor...',
    active: 'Aktif',
    passive: 'Pasif',
    createAria: 'Yeni sektor olustur',
    editAria: 'Sektoru duzenle',
    createTitle: 'Yeni sektor olustur',
    editTitle: 'Sektoru duzenle',
    select: 'Seciniz',
    sectorGroupField: 'Sektor grubu',
    sectorRequired: 'Sektor grubu, ad ve kod zorunludur.',
    sectorLoadError: 'Sektorler yuklenemedi.',
    sectorLoadErrorDetail: 'Sektorler yuklenirken hata olustu.',
    sectorGroupsLoadError: 'Sektor gruplari yuklenemedi.',
    sectorGroupsLoadErrorDetail: 'Sektor gruplari yuklenirken hata olustu.',
    saveFailed: 'Islem basarisiz oldu.',
    operationError: 'Islem sirasinda hata olustu.',
    created: 'Sektor olusturuldu.',
    updated: 'Sektor guncellendi.',
    deleteConfirm: 'Bu sektoru silmek istediginizden emin misiniz?',
    deleteFailed: 'Silme islemi basarisiz oldu.',
    deleteError: 'Silme sirasinda hata olustu.',
    deleted: 'Sektor silindi.',
    sectorPlaceholder: 'Bilgisayar Programlama'
  },
  'en-US': {
    pageTitle: 'Sectors',
    pageDescription: 'Sub-sector definitions from EVDS NACE Rev.2.',
    newSector: 'New sector',
    sectorGroup: 'Sector Group',
    sector: 'Sector',
    code: 'Code',
    status: 'Status',
    action: 'Action',
    loading: 'Loading...',
    noRecords: 'No sector definition found yet.',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    save: 'Save',
    update: 'Update',
    cancel: 'Cancel',
    saving: 'Saving...',
    active: 'Active',
    passive: 'Passive',
    createAria: 'Create new sector',
    editAria: 'Edit sector',
    createTitle: 'Create new sector',
    editTitle: 'Edit sector',
    select: 'Select',
    sectorGroupField: 'Sector group',
    sectorRequired: 'Sector group, name, and code are required.',
    sectorLoadError: 'Sectors could not be loaded.',
    sectorLoadErrorDetail: 'An error occurred while loading sectors.',
    sectorGroupsLoadError: 'Sector groups could not be loaded.',
    sectorGroupsLoadErrorDetail: 'An error occurred while loading sector groups.',
    saveFailed: 'Operation failed.',
    operationError: 'An error occurred during operation.',
    created: 'Sector created.',
    updated: 'Sector updated.',
    deleteConfirm: 'Are you sure you want to delete this sector?',
    deleteFailed: 'Delete operation failed.',
    deleteError: 'An error occurred during delete.',
    deleted: 'Sector deleted.',
    sectorPlaceholder: 'Computer Programming'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const createFormState = () => ({
  id: null,
  sectorGroupId: '',
  name: '',
  code: '',
  status: 'Aktif'
});

function SectorsPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [sectors, setSectors] = useState([]);
  const [sectorGroups, setSectorGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState(createFormState());
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isEditMode = Boolean(formState.id);
  const statusLabel = useCallback((value) => (value === 'Aktif' ? t('active') : t('passive')), [t]);

  const buildHeaders = useCallback(() => {
    const token = sessionStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, []);

  const loadSectors = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/sectors`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('sectorLoadError'));
      setSectors(await response.json());
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('sectorLoadErrorDetail'));
    } finally {
      setLoading(false);
    }
  }, [buildHeaders, t]);

  useEffect(() => {
    loadSectors();
  }, [loadSectors]);

  const loadSectorGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sector-groups`, { headers: buildHeaders() });
      if (!response.ok) throw new Error(t('sectorGroupsLoadError'));
      setSectorGroups(await response.json());
    } catch (caught) {
      console.error(caught instanceof Error ? caught.message : t('sectorGroupsLoadErrorDetail'));
    }
  }, [buildHeaders, t]);

  useEffect(() => {
    loadSectorGroups();
  }, [loadSectorGroups]);

  const resetForm = useCallback(() => {
    setFormState(createFormState());
    setFormError('');
  }, []);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const preparePayload = useMemo(() => () => ({
    sectorGroupId: Number(formState.sectorGroupId),
    name: formState.name.trim(),
    code: formState.code.trim(),
    status: formState.status
  }), [formState.code, formState.name, formState.sectorGroupId, formState.status]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    const payload = preparePayload();
    if (!payload.sectorGroupId || !payload.name || !payload.code) {
      setFormError(t('sectorRequired'));
      setSaving(false);
      return;
    }

    const method = isEditMode ? 'PUT' : 'POST';
    const url = `${API_BASE_URL}/api/sectors${isEditMode ? `/${formState.id}` : ''}`;

    try {
      const response = await fetch(url, {
        method,
        headers: buildHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('saveFailed'));
      }

      setSuccess(isEditMode ? t('updated') : t('created'));
      closeModal();
      await loadSectors();
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : t('operationError'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (sector) => {
    setFormState({
      id: sector.id,
      sectorGroupId: sector.sectorGroupId ? String(sector.sectorGroupId) : '',
      name: sector.name || '',
      code: sector.code || '',
      status: sector.status || 'Aktif'
    });
    setSuccess('');
    setFormError('');
    setIsModalOpen(true);
  };

  const handleDelete = async (sectorId) => {
    if (!window.confirm(t('deleteConfirm'))) return;

    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/sectors/${sectorId}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || t('deleteFailed'));
      }

      setSuccess(t('deleted'));
      if (formState.id === sectorId) closeModal();
      await loadSectors();
    } catch (caught) {
      setListError(caught instanceof Error ? caught.message : t('deleteError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card sectors-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <button type="button" className="btn-secondary" onClick={openCreateModal}>
            {t('newSector')}
          </button>
        }
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="sectors-grid">
        <section className="sectors-table-panel">
          <div className="table-wrapper">
            <table className="sectors-table">
              <thead>
                <tr>
                  <th>{t('sectorGroup')}</th>
                  <th>{t('sector')}</th>
                  <th>{t('code')}</th>
                  <th>{t('status')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="table-placeholder">{t('loading')}</td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan={5} className="table-placeholder">{listError}</td>
                  </tr>
                ) : sectors.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="table-placeholder">{t('noRecords')}</td>
                  </tr>
                ) : (
                  sectors.map((sector) => (
                    <tr key={sector.id || sector.code}>
                      <td>
                        <strong>{sector.sectorGroupCode}</strong>
                        <div className="muted">{sector.sectorGroupName}</div>
                      </td>
                      <td>{sector.name}</td>
                      <td>{sector.code}</td>
                      <td>
                        <span className={sector.status === 'Aktif' ? 'badge badge-active' : 'badge'}>{statusLabel(sector.status)}</span>
                      </td>
                      <td className="action-cell">
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-edit"
                          onClick={() => handleEdit(sector)}
                          aria-label={t('edit')}
                          title={t('edit')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-delete"
                          onClick={() => handleDelete(sector.id)}
                          aria-label={t('delete')}
                          title={t('delete')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditMode ? t('editTitle') : t('createTitle')} width="560px">
        <section>
          {formError && <div className="alert alert-error">{formError}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="sectorGroupId">{t('sectorGroupField')}</label>
              <select
                id="sectorGroupId"
                name="sectorGroupId"
                value={formState.sectorGroupId}
                onChange={(event) => setFormState((current) => ({ ...current, sectorGroupId: event.target.value }))}
                required
              >
                <option value="">{t('select')}</option>
                {sectorGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.code} - {group.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="name">{t('sector')}</label>
              <input
                id="name"
                name="name"
                value={formState.name}
                onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
                placeholder={t('sectorPlaceholder')}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="code">{t('code')}</label>
              <input
                id="code"
                name="code"
                value={formState.code}
                onChange={(event) => setFormState((current) => ({ ...current, code: event.target.value }))}
                placeholder="62"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="status">{t('status')}</label>
              <select
                id="status"
                name="status"
                value={formState.status}
                onChange={(event) => setFormState((current) => ({ ...current, status: event.target.value }))}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>{statusLabel(option)}</option>
                ))}
              </select>
            </div>
            <div className="form-footer">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? t('saving') : isEditMode ? t('update') : t('save')}
              </button>
              <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </section>
      </Modal>
    </div>
  );
}

export default SectorsPage;
